// v77.99.48 — ParentName
//
// Inline display component for a parent's name with a small
// "(placeholder)" badge + tooltip when the member is a placeholder
// co-parent (an entity tracked locally but who hasn't signed up to
// the app yet).
//
// Why a dedicated component:
//   • Centralises the visual treatment so every surface that shows a
//     paying / receiving / sharing-with parent looks identical.
//   • Pairs the badge with a short explanatory tooltip — the in-app
//     parallel to the Maintenance / Expenses PDF "(placeholder)"
//     suffix, so the UI matches the document a third-party reader
//     (mediator / solicitor) will receive.
//   • Keeps the calling site dead simple: pass `id` + `members` and
//     it does the lookup, or pass an explicit `name` + `isPlaceholder`
//     if the caller already has the resolved data.
//
// Usage:
//   <ParentName id={arr.paying_parent_id} members={members} />
//   <ParentName name="Meg" isPlaceholder />

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const PLACEHOLDER_HELP =
  "Placeholder = a co-parent you've added locally but who hasn't signed up yet. Promote them to a real account any time — no history is lost.";

export function ParentName({
  id,
  members = [],
  name,
  isPlaceholder,
  fallback = "—",
  className = "",
  badgeClassName = "",
  testid,
}) {
  // Resolve from `members` lookup when `name` wasn't explicitly passed.
  let displayName = name;
  let placeholder = isPlaceholder;
  if (displayName === undefined && id) {
    const m = members.find((x) => x.id === id);
    if (m) {
      displayName = m.name || "";
      placeholder = !!m.is_placeholder;
    }
  }
  if (!displayName) {
    return <span className={className} data-testid={testid}>{fallback}</span>;
  }
  if (!placeholder) {
    return <span className={className} data-testid={testid}>{displayName}</span>;
  }
  return (
    <span className={`inline-flex items-baseline gap-1.5 ${className}`} data-testid={testid}>
      <span>{displayName}</span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              tabIndex={0}
              className={`text-[10px] uppercase tracking-[0.06em] px-1.5 py-0.5 rounded-sm border border-[#C99A2A] bg-[#FBEFCC] text-[#6F4E1E] font-medium leading-none cursor-help focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C99A2A]/60 ${badgeClassName}`}
              aria-label={`${displayName} is a placeholder co-parent`}
              data-testid={testid ? `${testid}-placeholder-badge` : "parent-name-placeholder-badge"}
            >
              placeholder
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="max-w-[260px] text-xs leading-relaxed bg-[#273131] text-white border-[#273131]"
          >
            {PLACEHOLDER_HELP}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </span>
  );
}
